import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * OUTGLOW fix O01 (21/10/2026): os reels tocavam sozinhos mesmo com economia de dados ou rede
 * 2g/3g (onde o vídeo nem chega a tocar e só gasta o plano). Nesses casos, e com reduced motion,
 * fica só o poster com o botão play; o arquivo só baixa se a pessoa tocar.
 * @returns {boolean}
 */
function canAutoplay() {
  if (prefersReducedMotion()) return false;
  /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */
  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData) return false;
  return !['slow-2g', '2g', '3g'].includes(connection?.effectiveType ?? '');
}

/** OUTGLOW fix O01 (21/10/2026): mesma quebra do CSS da seção (749px) para escolher 720p no desktop. */
const desktopQuery = window.matchMedia('(min-width: 750px)');

/**
 * Plays only the reels that are on screen. Each video keeps its file in
 * data-src (<=480p) / data-src-desktop (<=720p) until it first nears the viewport,
 * so offscreen reels cost nothing. With reduced motion, data saver or a 2g/3g
 * connection the poster stays and nothing autoplays. Each reel has a play/pause
 * button; what the person chose there wins over autoplay (OUTGLOW fix B24).
 *
 * The track scrolls natively with touch; mouse and pen can drag it, and the
 * arrows move it one card at a time.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling row of reels.
 * @property {HTMLButtonElement} [prev] - Previous arrow.
 * @property {HTMLButtonElement} [next] - Next arrow.
 * @property {HTMLVideoElement[]} [videos] - The reel videos.
 *
 * @extends Component<Refs>
 */
class VideoReelsComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {{ id: number, x: number, scrollLeft: number, moved: boolean } | null} */
  #drag = null;

  /** OUTGLOW fix O01 (21/10/2026): decidido uma vez ao conectar (ver canAutoplay). */
  #autoplay = true;

  connectedCallback() {
    super.connectedCallback();
    this.#setUpNavigation();

    // OUTGLOW fix O01/B24 (21/10/2026): antes, com reduced motion, saía aqui e o vídeo nunca tocava.
    // Agora o observer roda sempre (pausa o que sai da tela e toca o que a pessoa pediu), e o
    // autoplay só acontece quando canAutoplay() deixa.
    this.#autoplay = canAutoplay();
    this.toggleAttribute('data-no-autoplay', !this.#autoplay);

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '200px 0px',
      threshold: 0.2,
    });
    for (const video of this.refs.videos ?? []) this.#observer.observe(video);

    document.addEventListener('visibilitychange', this.#handleVisibility);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setUpNavigation();
    if (!this.#observer) return;

    this.#observer.disconnect();
    for (const video of this.refs.videos ?? []) this.#observer.observe(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#abort?.abort();
    this.#abort = null;
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  #setUpNavigation() {
    const { track, prev, next } = this.refs;
    if (!track) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    prev?.addEventListener('click', () => this.#step(-1), { signal });
    next?.addEventListener('click', () => this.#step(1), { signal });
    track.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
    track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    track.addEventListener('pointermove', this.#handlePointerMove, { signal });
    track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    track.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    // OUTGLOW fix B24 (21/10/2026): botão pausar/reproduzir de cada reel. play/pause não sobem
    // (bubble), mas passam pela fase de captura do track, então um listener só cobre todos.
    track.addEventListener('click', this.#handleToggle, { signal });
    track.addEventListener('play', this.#syncToggle, { capture: true, signal });
    track.addEventListener('pause', this.#syncToggle, { capture: true, signal });
    // OUTGLOW fix O01 (21/10/2026): a rendição 480p/720p é derivada pelo nome no Liquid; se não existir, volta ao mp4 original.
    track.addEventListener('error', this.#handleVideoError, { capture: true, signal });
    // A drag ends with a click on whatever is under the pointer; swallow it.
    track.addEventListener(
      'click',
      (event) => {
        if (this.#drag?.moved) event.preventDefault();
        this.#drag = null;
      },
      { capture: true, signal }
    );

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#updateArrows);
    this.#resizeObserver.observe(track);
    this.#updateArrows();
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const card = track.firstElementChild;
    if (!(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #updateArrows = () => {
    const { track, prev, next } = this.refs;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    this.classList.toggle('is-scrollable', maxScroll > 1);
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Touch already scrolls natively; only mouse and pen need dragging.
    if (event.pointerType === 'touch' || event.button !== 0) return;

    this.#drag = { id: event.pointerId, x: event.clientX, scrollLeft: this.refs.track.scrollLeft, moved: false };
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;

    const { track } = this.refs;
    const delta = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(delta) < 5) return;
      drag.moved = true;
      track.setPointerCapture(event.pointerId);
      track.classList.add('is-dragging');
    }
    track.scrollLeft = drag.scrollLeft - delta;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;

    const { track } = this.refs;
    if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    track.classList.remove('is-dragging');
    // Keep the drag around only if it moved, so the click that follows is swallowed.
    if (!drag.moved) this.#drag = null;
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!(target instanceof HTMLVideoElement)) continue;

      if (isIntersecting) {
        if (this.#shouldPlay(target)) this.#play(target);
      } else {
        target.pause();
      }
    }
  };

  #handleVisibility = () => {
    for (const video of this.refs.videos ?? []) {
      if (document.hidden) video.pause();
    }
    // Back on the tab, the observer callback does not refire; resume the visible ones.
    if (!document.hidden) {
      for (const video of this.refs.videos ?? []) {
        const { top, bottom } = video.getBoundingClientRect();
        if (bottom > 0 && top < window.innerHeight && this.#shouldPlay(video)) this.#play(video);
      }
    }
  };

  /**
   * OUTGLOW fix B24 (21/10/2026): o que a pessoa escolheu no botão (data-intent) vale mais que o autoplay.
   * @param {HTMLVideoElement} video
   */
  #shouldPlay(video) {
    const { intent } = video.dataset;
    return intent ? intent === 'play' : this.#autoplay;
  }

  /** @param {MouseEvent} event */
  #handleToggle = (event) => {
    // Um arrasto com o mouse termina num click já cancelado (listener de captura acima).
    if (event.defaultPrevented || !(event.target instanceof Element)) return;
    const button = event.target.closest('.video-reel__toggle');
    const video = button?.closest('.video-reel')?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.dataset.intent = 'play';
      this.#play(video);
    } else {
      video.dataset.intent = 'pause';
      video.pause();
    }
  };

  /** @param {Event} event */
  #syncToggle = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const reel = video.closest('.video-reel');
    const button = reel?.querySelector('.video-reel__toggle');
    if (!(button instanceof HTMLElement)) return;

    reel?.toggleAttribute('data-playing', !video.paused);
    const label = video.paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  };

  /** @param {Event} event */
  #handleVideoError = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const { srcFull } = video.dataset;
    if (!srcFull || !video.getAttribute('src') || video.getAttribute('src') === srcFull) return;

    video.src = srcFull;
    if (this.#shouldPlay(video)) video.play().catch(() => {});
  };

  /** @param {HTMLVideoElement} video */
  #play(video) {
    // OUTGLOW fix O01 (21/10/2026): <=480p no celular, <=720p no desktop (antes era sempre 1080p).
    if (!video.src && video.dataset.src) {
      const { srcDesktop } = video.dataset;
      video.src = srcDesktop && desktopQuery.matches ? srcDesktop : video.dataset.src;
    }
    // Autoplay can be refused (low power mode); the poster stays in that case.
    video.play().catch(() => {});
  }
}

if (!customElements.get('video-reels-component')) {
  customElements.define('video-reels-component', VideoReelsComponent);
}
